import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import { AiSuggestionsEntryPointValue } from '@atlaskit/editor-common/analytics/ai-suggestions-entry-points';
import { isOfflineMode } from '@atlaskit/editor-common/connectivity/isOfflineMode';
import {
	AI_SECTION,
	AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR,
	AI_SECTION_COMMENT_PRIMARY_TOOLBAR,
	AI_SECTION_PRIMARY_TOOLBAR,
	AI_SUGGESTIONS_GROUP,
	AI_SUGGESTIONS_BUTTON,
	ROVO_GROUP_DISPLAY_SMALL,
} from '@atlaskit/editor-common/toolbar/keys';
import {
	AI_SECTION_RANK,
	AI_SUGGESTIONS_GROUP_RANK,
	ROVO_GROUP_DISPLAY_SMALL_RANK,
} from '@atlaskit/editor-common/toolbar/rank';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type {
	RegisterComponent,
	ToolbarComponentTypes,
} from '@atlaskit/editor-toolbar-model/types';
import { Show } from '@atlaskit/editor-toolbar/show';
import { ToolbarButton } from '@atlaskit/editor-toolbar/toolbar-button';
import { ToolbarButtonGroup } from '@atlaskit/editor-toolbar/toolbar-button-group';
import TextReviewIcon from '@atlaskit/icon-lab/core/text-review';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import {
	createEntryPointExposureSession,
	type EntryPointExposureSession,
	useEntryPointExposureAnalytics,
} from './use-entry-point-exposure-analytics';

const isResponsiveFullPageReview = (parents: ToolbarComponentTypes) =>
	parents.some(
		(parent) =>
			parent.key === AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR.key ||
			parent.key === AI_SECTION_PRIMARY_TOOLBAR.key,
	) && UNSAFE_expValNoExposure('platform_editor_ai_content_suggested_edits', 'isEnabled', false);

const AiSuggestionsButton = ({
	api,
	exposureSession,
	isIconOnly = false,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	exposureSession: EntryPointExposureSession;
	isIconOnly?: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const label = formatMessage(aiSuggestionsMessages.suggestEditsButtonLabel);
	const exposureRef = useEntryPointExposureAnalytics({
		api,
		entryPoint: AiSuggestionsEntryPointValue.PRIMARY_TOOLBAR,
		exposureSession,
	});
	const { isThinking, isOffline } = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions', 'connectivity'],
		(state) => ({
			isThinking: state.aiSuggestionsState?.thinkingStatus === 'thinking',
			isOffline: isOfflineMode(state.connectivityState?.mode),
		}),
	);
	const isDisabled = isThinking || isOffline;

	const handleClick = useCallback(() => {
		if (isDisabled) {
			return;
		}
		api?.aiSuggestions?.actions.setThinkingState('thinking', {
			entryPoint: AiSuggestionsEntryPointValue.PRIMARY_TOOLBAR,
		});
	}, [api, isDisabled]);

	return (
		<Tooltip
			content={formatMessage(aiSuggestionsMessages.suggestEditsButtonTooltip)}
			position="top"
		>
			<ToolbarButton
				ref={exposureRef}
				onClick={handleClick}
				testId="editor-toolbar__ai-suggestions-button"
				label={label}
				iconBefore={<TextReviewIcon label="" size="small" />}
				ignoreInteractionDisabled
				isDisabled={isDisabled}
			>
				{isIconOnly ? undefined : label}
			</ToolbarButton>
		</Tooltip>
	);
};

export const AISuggestionsToolbarComponents = ({
	api,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
}): RegisterComponent[] => {
	// Scope exposure tracking to this toolbar registration, not the whole browser session.
	const exposureSession = createEntryPointExposureSession();

	return [
		// AI SUGGESTIONS GROUP — only registered in the pinned primary toolbar sections,
		// not in AI_SECTION (which is used by the floating toolbar).
		//
		// Full-page Review keeps its label through md; other toolbar appearances retain lg.
		{
			type: AI_SUGGESTIONS_GROUP.type,
			key: AI_SUGGESTIONS_GROUP.key,
			parents: [
				{
					type: AI_SECTION.type,
					key: AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[AI_SUGGESTIONS_GROUP.key],
				},
				{
					type: AI_SECTION.type,
					key: AI_SECTION_COMMENT_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[AI_SUGGESTIONS_GROUP.key],
				},
				{
					type: AI_SECTION_PRIMARY_TOOLBAR.type,
					key: AI_SECTION_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[AI_SUGGESTIONS_GROUP.key],
				},
			],
			component: ({ children, parents }) =>
				isResponsiveFullPageReview(parents) ? (
					<>
						<Show above="md">
							<ToolbarButtonGroup>{children}</ToolbarButtonGroup>
						</Show>
						<Show below="md">
							<ToolbarButtonGroup>
								<AiSuggestionsButton api={api} exposureSession={exposureSession} isIconOnly />
							</ToolbarButtonGroup>
						</Show>
					</>
				) : (
					<Show above="lg">
						<ToolbarButtonGroup>{children}</ToolbarButtonGroup>
					</Show>
				),
		},
		// Full-page Review renders both responsive variants in its own group.
		// Other appearances retain their compact Rovo placement.
		{
			type: AI_SUGGESTIONS_BUTTON.type,
			key: AI_SUGGESTIONS_BUTTON.key,
			parents: [
				{
					type: AI_SUGGESTIONS_GROUP.type,
					key: AI_SUGGESTIONS_GROUP.key,
					rank: AI_SUGGESTIONS_GROUP_RANK[AI_SUGGESTIONS_BUTTON.key],
				},
				{
					type: ROVO_GROUP_DISPLAY_SMALL.type,
					key: ROVO_GROUP_DISPLAY_SMALL.key,
					rank: ROVO_GROUP_DISPLAY_SMALL_RANK[AI_SUGGESTIONS_BUTTON.key],
				},
			],
			component: ({ parents = [] }) => {
				const isCompactFullPageReview =
					isResponsiveFullPageReview(parents) &&
					parents.some((parent) => parent.key === ROVO_GROUP_DISPLAY_SMALL.key);

				return isCompactFullPageReview ? null : (
					<AiSuggestionsButton api={api} exposureSession={exposureSession} />
				);
			},
		},
	];
};
